import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import PageHeader from '@/components/ui/page-header';
import { useAuth } from "@/contexts/AuthContext";
import { login } from '@/api/auth'
import { useQueryClient } from '@tanstack/react-query';
import { fetchCurrentUser } from '@/api/user';
import { ResponsiveContainer } from '@/components/ResponsiveContainer';

const Login = () => {
  const navigate = useNavigate();
  const { setToken, setAuthenticated, setUser } = useAuth();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    const authResponse = await login(formData.email, formData.password); // get user info from backend
    if (authResponse && typeof authResponse === 'object' && 'token' in authResponse) {
      await setToken(authResponse.token);
      setAuthenticated(true);
      
      const user = await queryClient.fetchQuery({
        queryKey: ['currentUser'],
        queryFn: fetchCurrentUser,
      });
      
      // Update AuthContext with user data
      setUser(user);
      
      // Navigate to module router which will determine the correct route
      // based on the user's current in-progress module
      navigate('/module');
    } else {
      setErrors({ email: 'Invalid email or password' });
    }
      
    setIsLoading(false);

  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-white pb-20 safe-area-inset">
      <PageHeader 
        title="Login"
        variant="title-only"
      />

      {/* Content */}
      <ResponsiveContainer maxWidth="lg" className="flex-1 flex flex-col justify-start pt-8 sm:pt-16 md:pt-24">
        <div className="mb-8">
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-black mb-6 leading-tight">
            Login
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 lg:max-w-md">
          <div>
            <Label htmlFor="email" className="text-black font-medium text-lg">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`mt-2 h-12 sm:h-14 rounded-lg text-base sm:text-lg ${errors.email ? 'border-red-500' : 'border-[#cccccc]'}`}
              placeholder="Enter your email address"
              autoComplete="email"
              autoFocus
            />
            {errors.email && (
              <p className="text-red-500 text-sm sm:text-base mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label htmlFor="password" className="text-black font-medium text-lg">
              Password
            </Label>
            <div className="relative mt-2">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className={`h-12 sm:h-14 pr-12 rounded-lg text-base sm:text-lg ${errors.password ? 'border-red-500' : 'border-[#cccccc]'}`}
                placeholder="Enter your password"
                autoComplete="current-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#666666] hover:text-black transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm sm:text-base mt-1">{errors.password}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-black text-white hover:bg-[#666666] h-12 sm:h-14 rounded-lg text-base sm:text-lg font-medium min-touch-target touch-manipulation transition-colors"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        <div className="text-center mt-6 lg:max-w-md">
          <p className="text-sm sm:text-base lg:text-lg text-[#666666]">
            Don't have an account?{' '}
            <button
              onClick={() => navigate('/register')}
              className="text-black font-medium hover:underline transition-all"
            >
              Create profile
            </button>
          </p>
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default Login;
